import { defineComponent as _defineComponent } from 'vue'
import device from 'bits/device'
import { isElementInViewport } from 'bits/dom'
import { __ } from 'bits/intl'
import { OzTextButtonColorScheme } from 'library/v4/components/OzTextButton.vue'
import { storeToRefs } from 'pinia'
import { useSurfaceStore } from 'pinia/surface'
import { useSurfaceMapStore } from 'pinia/surface_map'
import { useSurfacePermissionsStore } from 'pinia/surface_permissions'
import { useSurfacePostsStore } from 'pinia/surface_posts'
import { useSurfaceSectionsStore } from 'pinia/surface_sections'
import { useSurfaceWishArrangementStore } from 'pinia/surface_wish_arrangement'
import { useWindowSizeStore } from 'pinia/window_size'
import type { Section } from 'types'
import { computed, defineAsyncComponent, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import SurfaceSectionTitle from 'vuecomponents/SurfaceSectionTitle.vue'
import type { LongPressEvent } from 'vuedirectives/emit_long_press'


export default /*#__PURE__*/_defineComponent({
  __name: 'SurfacePostGroupHeaderSection',
  props: {
    section: null,
    useDefaultBgColor: { type: Boolean }
  },
  emits: ["long-press", "resize", "before-unmount"],
  setup(__props: any, { emit }: { emit: ({
  (name: 'long-press', event: LongPressEvent): void
  (name: 'resize', value: DOMRectReadOnly): void
  (name: 'before-unmount'): void
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
  section: Required<Section>
  useDefaultBgColor?: boolean
}

const OzBaseButton = defineAsyncComponent(() => import('library/v4/components/OzBaseButton.vue'))
const OzSkewedButton = defineAsyncComponent(() => import('library/v4/components/OzSkewedButton.vue'))
const OzTextButton = defineAsyncComponent(() => import('library/v4/components/OzTextButton.vue'))
const OzIcon = defineAsyncComponent(() => import('library/v4/components/OzIcon.vue'))





const surfacePostsStore = useSurfacePostsStore()
const surfaceSectionsStore = useSurfaceSectionsStore()
const surfaceMapStore = useSurfaceMapStore()

const {
  backgroundColor,
  backgroundLuminance,
  colorScheme,
  dir,
  fontId,
  format,
  isGrid,
  isMap,
  isReadOnly,
  skinPrimaryVariantColorAsRGB,
} = storeToRefs(useSurfaceStore())
const { isAPostDragging } = storeToRefs(surfacePostsStore)
const { isSectionedAndNonmanualSorted } = storeToRefs(useSurfaceWishArrangementStore())
const { canIModerate, isPostable } = storeToRefs(useSurfacePermissionsStore())
const { sectionBeingRenamedId, sectionUnderActionId, sectionUnderActionRightClickCoordinates } =
  storeToRefs(surfaceSectionsStore)
const { postListPosition } = storeToRefs(surfaceMapStore)
const { isSmallerThanDesktop, isSmallerThanTabletLandscape, isSmallerThanTabletPortrait } = storeToRefs(
  useWindowSizeStore(),
)

const isAPostDraggingAndNonmanualSorted = computed(() => {
  return isAPostDragging.value && isSectionedAndNonmanualSorted.value
})
const hideWhenDraggingaPost = computed(() => !isGrid.value && isAPostDraggingAndNonmanualSorted.value)

function openSectionActionMenu(sectionId: Section['id'], e?: MouseEvent): void {
  if (sectionId) {
    sectionUnderActionId.value = sectionId
    const isOpeningAtCoordinates = e?.pageX != null && e?.pageY != null
    if (isOpeningAtCoordinates) {
      sectionUnderActionRightClickCoordinates.value = { x: e.pageX, y: e.pageY }
    } else {
      sectionUnderActionRightClickCoordinates.value = null
    }
  }
}

function startEditingSectionTitle(sectionId: Section['id']): void {
  if (sectionId) surfaceSectionsStore.startRenamingSection(sectionId)
}

const sectionTitleRef = ref<InstanceType<typeof SurfaceSectionTitle> | null>(null)

function finishEditingSectionTitle(newTitle): void {
  if (sectionBeingRenamedId.value == null) return
  const edited = surfaceSectionsStore.editSectionTitle({ sectionId: sectionBeingRenamedId.value, title: newTitle })
  if (edited) {
    surfaceSectionsStore.stopRenamingSection()
  } else {
    // re-focus the section title input
    sectionTitleRef.value?.selectTitleInput()
  }
}

const $el = ref<HTMLElement | null>(null)

function emitHeaderResize(): void {
  if ($el.value) {
    emit('resize', $el.value.getBoundingClientRect())
  }
}

watch(sectionBeingRenamedId, (newSectionBeingRenamedId) => {
  if (newSectionBeingRenamedId == null) return
  nextTick(() => {
    const element = document.getElementById(`section-title-${newSectionBeingRenamedId}`)
    if (!isElementInViewport(element)) {
      element?.scrollIntoView(true)
    }
  })
})

onBeforeUnmount(() => {
  emit('before-unmount')
})

const sectionAddPostButton = computed(() => ({
  shouldShow: isPostable.value && !isReadOnly.value,
  icon: 'plus_thin',
  label: __('Add post to this section'),
  onClick: () => {
    if (isMap.value) {
      surfaceMapStore.startPickingLocation({ sectionId: props.section.id })
    } else {
      surfacePostsStore.startNewPost({ attributes: { wall_section_id: props.section.id } })
    }
  },
  id: `section-add-post-button-${props.section.id}`,
  testId: `${props.section.title}SectionAddPostButton`,
}))

const sectionActionsButton = computed(() => ({
  shouldShow: !isReadOnly.value,
  icon: 'dot_3_horizontal',
  label: __('Open section actions menu'),
  onClick: () => openSectionActionMenu(props.section.id),
  id: `section-action-menu-button-${props.section.id}`,
  testId: `${props.section.title}SectionTitleMoreButton`,
}))

const sectionSpecificButtons = computed(() =>
  [sectionAddPostButton.value, sectionActionsButton.value].filter((button) => button.shouldShow),
)

// https://whimsical.com/sections-0-1-stream-UwXiARuawScRnEGKF2bScC@2bsEvpTYFZsxPP7WckcQqNHxJtrkdXQAyBZ
const xSectionSpecificButtons = computed(() => {
  if (isGrid.value) return false
  if (isMap.value) return postListPosition.value === 'side'
  if (device.touchable) return !isSmallerThanDesktop.value
  return !isSmallerThanTabletPortrait.value
})

const allowOpenSectionActionMenu = computed(() => {
  // grid has no section specific buttons so handle accordlingly
  if (isGrid.value && isSmallerThanTabletPortrait.value) return true
  if (isGrid.value && !isSmallerThanTabletPortrait.value) return false
  return device.touchable || !xSectionSpecificButtons.value
})

return { __sfc: true,OzBaseButton, OzSkewedButton, OzTextButton, OzIcon, props, emit, surfacePostsStore, surfaceSectionsStore, surfaceMapStore, backgroundColor, backgroundLuminance, colorScheme, dir, fontId, format, isGrid, isMap, isReadOnly, skinPrimaryVariantColorAsRGB, isAPostDragging, isSectionedAndNonmanualSorted, canIModerate, isPostable, sectionBeingRenamedId, sectionUnderActionId, sectionUnderActionRightClickCoordinates, postListPosition, isSmallerThanDesktop, isSmallerThanTabletLandscape, isSmallerThanTabletPortrait, isAPostDraggingAndNonmanualSorted, hideWhenDraggingaPost, openSectionActionMenu, startEditingSectionTitle, sectionTitleRef, finishEditingSectionTitle, $el, emitHeaderResize, sectionAddPostButton, sectionActionsButton, sectionSpecificButtons, xSectionSpecificButtons, allowOpenSectionActionMenu, OzTextButtonColorScheme, SurfaceSectionTitle }
}

})